import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A web component that wraps text content and provides read more/less functionality
 * using CSS line-clamp.
 */
class ReadMoreWrapper extends HTMLElement {
  /** @type {boolean} */
  #expanded = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {string} */
  #readMoreText = '';

  /** @type {string} */
  #readLessText = '';

  /** @type {HTMLElement | null} */
  #content = null;

  /** @type {HTMLButtonElement | null} */
  #toggleButton = null;

  connectedCallback() {
    // Wait for children to be available
    requestAnimationFrame(() => {
      this.#content = this.querySelector('[data-ref="content"]');
      this.#toggleButton = this.querySelector('[data-ref="toggleButton"]');

      if (!this.#content || !this.#toggleButton) {
        return;
      }

      this.#readMoreText = this.dataset.readMoreText || 'Read more';
      this.#readLessText = this.dataset.readLessText || 'Read less';

      this.#toggleButton.addEventListener('click', this.#toggle);
      this.#setupResizeObserver();

      // Wait for styles to be applied before checking overflow
      requestAnimationFrame(() => {
        this.#checkOverflow();
      });
    });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#toggleButton?.removeEventListener('click', this.#toggle);
  }

  #setupResizeObserver() {
    if (!this.#content) return;

    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.#expanded) {
        this.#checkOverflow();
      }
    });
    this.#resizeObserver.observe(this.#content);
  }

  #checkOverflow() {
    const content = this.#content;
    const toggleButton = this.#toggleButton;

    if (!content || !toggleButton) return;

    // Temporarily remove line-clamp to measure true content height
    const originalLineClamp =
      content.style.getPropertyValue('-webkit-line-clamp');
    const originalDisplay = content.style.display;

    content.style.setProperty('-webkit-line-clamp', 'unset');
    content.style.display = 'block';

    const fullHeight = content.scrollHeight;

    // Restore line-clamp
    if (originalLineClamp) {
      content.style.setProperty('-webkit-line-clamp', originalLineClamp);
    } else {
      content.style.removeProperty('-webkit-line-clamp');
    }
    if (originalDisplay) {
      content.style.display = originalDisplay;
    } else {
      content.style.removeProperty('display');
    }

    const clampedHeight = content.clientHeight;
    const isOverflowing = fullHeight > clampedHeight;
    toggleButton.hidden = !isOverflowing;
  }

  /**
   * Returns the appropriate line clamp value based on current breakpoint
   * @returns {string}
   */
  #getLineClampValue() {
    const isMobile = isMobileBreakpoint();
    const mobileLines = this.dataset.linesMobile || '0';
    const desktopLines = this.dataset.linesDesktop || '0';
    return isMobile ? mobileLines : desktopLines;
  }

  /**
   * Toggles the expanded state of the content
   * @param {Event} event
   */
  #toggle = (event) => {
    event.preventDefault();
    const content = this.#content;
    const toggleButton = this.#toggleButton;

    if (!content || !toggleButton) return;

    this.#expanded = !this.#expanded;

    if (this.#expanded) {
      content.style.setProperty('--line-clamp', 'none');
      content.style.setProperty('-webkit-line-clamp', 'unset');
      toggleButton.textContent = this.#readLessText;
      this.dataset.expanded = 'true';
    } else {
      const lineClampValue = this.#getLineClampValue();
      content.style.setProperty('--line-clamp', lineClampValue);
      content.style.setProperty('-webkit-line-clamp', lineClampValue);
      toggleButton.textContent = this.#readMoreText;
      this.dataset.expanded = 'false';
      this.#checkOverflow();
    }

    toggleButton.setAttribute('aria-expanded', String(this.#expanded));
  };
}

if (!customElements.get('read-more-wrapper')) {
  customElements.define('read-more-wrapper', ReadMoreWrapper);
}
